import { useEffect, useState } from 'react'
import FilterListIcon from '@mui/icons-material/FilterList'
import { CrossIcon } from '../../Views/Success/LeadFeed/Icons'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Checkbox from '@mui/material/Checkbox'

interface Facility {
  id: string
  name: string
}

interface Props {
  unselectedFacilities: Facility[]
  activeFilters: string[]
  onFiltersChange: (filters: string[]) => void
  totalCount: number
  showFacilities?: boolean
}

const VenueFilterRow = ({
  unselectedFacilities,
  activeFilters,
  onFiltersChange,
  totalCount,
  showFacilities = true,
}: Props) => {
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null)
  const [menuFacilitiesSnapshot, setMenuFacilitiesSnapshot] = useState<Facility[]>([])
  const filterMenuOpen = Boolean(filterAnchorEl)

  useEffect(() => {
    if (filterMenuOpen) setMenuFacilitiesSnapshot(unselectedFacilities)
  }, [filterMenuOpen])

  const toggleFacility = (name: string) =>
    onFiltersChange(
      activeFilters.includes(name)
        ? activeFilters.filter((f) => f !== name)
        : [...activeFilters, name]
    )

  return (
    <div className='flex items-start justify-between'>
      <div className='flex items-start gap-4 flex-wrap'>
        {showFacilities && (
          <>
            <div className='relative'>
              <button
                type='button'
                onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                disabled={unselectedFacilities.length === 0}
                className='flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'>
                <FilterListIcon sx={{ width: 24, height: 24, color: '#af231c' }} />
                <span className='font-medium text-sm text-black tracking-[0.07px]'>Must have:</span>
              </button>
              <Menu
                anchorEl={filterAnchorEl}
                open={filterMenuOpen}
                onClose={() => setFilterAnchorEl(null)}
                PaperProps={{
                  sx: {
                    minWidth: 220,
                    maxHeight: 240,
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                    mt: 0.5,
                  },
                }}>
                {menuFacilitiesSnapshot.map((facility) => (
                  <MenuItem
                    key={facility.id}
                    id={`filter-facility-${facility.id}`}
                    selected={activeFilters.includes(facility.name)}
                    onClick={() => toggleFacility(facility.name)}
                    sx={{
                      fontSize: '14px',
                      py: 0.75,
                      px: 1.5,
                      '&.Mui-selected': {
                        backgroundColor: '#f0f0f0',
                        '&:hover': { backgroundColor: '#e8e8e8' },
                      },
                    }}>
                    <Checkbox
                      checked={activeFilters.includes(facility.name)}
                      size='small'
                      sx={{ p: 0, mr: 1 }}
                    />
                    {facility.name}
                  </MenuItem>
                ))}
              </Menu>
            </div>
            {activeFilters.map((filter) => (
              <div
                key={filter}
                className={`bg-[#f7f7f7] border border-[#bfbfbf] rounded-full px-2 py-1 flex items-center gap-2
                  text-sm font-medium text-black`}>
                {filter}
                <button
                  type='button'
                  onClick={() => onFiltersChange(activeFilters.filter((f) => f !== filter))}
                  className='cursor-pointer leading-none'
                  aria-label={`Remove ${filter} filter`}>
                  <CrossIcon width={12} height={12} />
                </button>
              </div>
            ))}
          </>
        )}
      </div>
      <div className='flex items-center gap-2 whitespace-nowrap'>
        <span className='font-medium text-sm text-black tracking-[0.07px]'>
          {totalCount} venue{totalCount !== 1 ? 's' : ''}
        </span>
      </div>
    </div>
  )
}

export default VenueFilterRow
